import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import {
    EMPTY_CELL,
    bresenhamLine,
    buildSvgDocument,
    countFilledCells,
    drawThickLine,
    fillEllipse,
    fillRect,
    floodFill,
    parseHexColor,
    renderPixels,
    stampBrush,
    CELL_SHAPE_OPTIONS,
    type CellShape,
    type OutputMode,
} from "../../utils/pixelSvgRender";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { cloneUint32Array } from "../../utils/undoSnapshots";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

export type ToolKind =
    | "pencil"
    | "eraser"
    | "bucket"
    | "rect"
    | "ellipse"
    | "line"
    | "eyedropper";

export const GRID_SIZE_OPTIONS = [16, 32, 64, 128, 256, 512] as const;
export type GridSize = (typeof GRID_SIZE_OPTIONS)[number];

const DEFAULT_GRID_SIZE: GridSize = 32;
const DEFAULT_COLOR = "#000000";
const DEFAULT_BRUSH_SIZE = 1;
const DEFAULT_OUTPUT_MODE: OutputMode = "greedy-rects";
const DEFAULT_OUTPUT_SIZE = 32;
const DEFAULT_CELL_SHAPE: CellShape = "square";
const MIN_OUTPUT_SIZE = 1;
const MAX_OUTPUT_SIZE = 8192;
const MAX_RECENT_COLORS = 12;

interface PixelSvgCanvasState {
    width: number;
    height: number;
    /** Source-of-truth pixel buffer (RGBA packed; 0 = empty). */
    pixelsRef: React.MutableRefObject<Uint32Array>;
    /** Bumps whenever pixels mutate so dependents re-render. */
    pixelsVersion: number;

    tool: ToolKind;
    setTool: (t: ToolKind) => void;

    brushSize: number;
    setBrushSize: (n: number) => void;

    color: string;
    setColor: (c: string) => void;

    recentColors: string[];

    outputMode: OutputMode;
    setOutputMode: (m: OutputMode) => void;

    outputSize: number;
    setOutputSize: (n: number) => void;

    /** SVG geometry per grid cell (export + output preview; editor stays a square grid). */
    cellShape: CellShape;
    setCellShape: (s: CellShape) => void;

    /** When false, the viewer hides the right-hand SVG preview pane (download still works). */
    showOutputPane: boolean;
    setShowOutputPane: (v: boolean) => void;

    /** Serialize current pixels to an SVG file download. */
    downloadSvg: () => void;
    /** Build the current rendered SVG output string. */
    buildExportSvgString: () => string;

    /** Editor-only: solid white behind the pixel grid (not exported). */
    editorWhiteBackground: boolean;
    setEditorWhiteBackground: (v: boolean) => void;

    gridSize: GridSize;
    setGridSize: (n: GridSize) => void;

    /** True if at least one cell is non-empty. */
    hasContent: boolean;

    /** Apply a stamp at a single cell with the current brush + color. */
    paintCell: (x: number, y: number) => void;
    /** Apply a stroke between two cells (Bresenham + brush). */
    paintStroke: (x0: number, y0: number, x1: number, y1: number) => void;
    /** Erase a stroke between two cells. */
    eraseStroke: (x0: number, y0: number, x1: number, y1: number) => void;
    /** Erase a single cell with the current brush. */
    eraseCell: (x: number, y: number) => void;
    /** Flood-fill at (x, y) with the current color. */
    fillAt: (x: number, y: number) => void;
    /** Commit a filled rectangle from (x0,y0) to (x1,y1). */
    commitRect: (x0: number, y0: number, x1: number, y1: number) => void;
    /** Commit a filled ellipse inscribed in the bounding box. */
    commitEllipse: (x0: number, y0: number, x1: number, y1: number) => void;
    /** Commit a thick line. */
    commitLine: (x0: number, y0: number, x1: number, y1: number) => void;
    /** Read a cell's color (or null if empty). */
    sampleColor: (x: number, y: number) => string | null;
    /** Restore the last non-eyedropper tool (used after a sample). */
    restoreLastPaintTool: () => void;

    /** Snapshot pixel state into the undo stack. Call BEFORE mutating. */
    snapshot: () => void;
    /** Mark pixels as modified after a mutation block (bumps version). */
    commit: () => void;

    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;

    clearAll: () => void;
    addRecentColor: (c: string) => void;

    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const PixelSvgCanvasContext = createContext<PixelSvgCanvasState | null>(null);

export function usePixelSvgCanvas(): PixelSvgCanvasState {
    const ctx = useContext(PixelSvgCanvasContext);
    if (!ctx) {
        throw new Error(
            "usePixelSvgCanvas must be used within PixelSvgCanvasProvider",
        );
    }
    return ctx;
}

const DEFAULT_PRESET: Record<string, unknown> = {
    gridSize: DEFAULT_GRID_SIZE,
    color: DEFAULT_COLOR,
    brushSize: DEFAULT_BRUSH_SIZE,
    outputMode: DEFAULT_OUTPUT_MODE,
    outputSize: DEFAULT_OUTPUT_SIZE,
    cellShape: DEFAULT_CELL_SHAPE,
    showOutputPane: true,
    editorWhiteBackground: false,
    tool: "pencil",
    recentColors: [],
};

interface PixelSvgCanvasProviderProps {
    children: ReactNode;
}

export function PixelSvgCanvasProvider({ children }: PixelSvgCanvasProviderProps) {
    const [gridSize, setGridSizeState] = useState<GridSize>(DEFAULT_GRID_SIZE);
    const [tool, setToolState] = useState<ToolKind>("pencil");
    const [brushSize, setBrushSizeState] = useState<number>(DEFAULT_BRUSH_SIZE);
    const [color, setColorState] = useState<string>(DEFAULT_COLOR);
    const [recentColors, setRecentColors] = useState<string[]>([]);
    const [outputMode, setOutputModeState] = useState<OutputMode>(DEFAULT_OUTPUT_MODE);
    const [outputSize, setOutputSizeState] = useState<number>(DEFAULT_OUTPUT_SIZE);
    const [showOutputPane, setShowOutputPaneState] = useState(true);
    const [editorWhiteBackground, setEditorWhiteBackgroundState] = useState(false);
    const [cellShape, setCellShapeState] = useState<CellShape>(DEFAULT_CELL_SHAPE);

    const pixelsRef = useRef<Uint32Array>(new Uint32Array(gridSize * gridSize));
    const [pixelsVersion, setPixelsVersion] = useState(0);
    const [hasContent, setHasContent] = useState(false);

    const history = useUndoRedo<Uint32Array>({ clone: cloneUint32Array });

    /** Last tool selected before switching to the eyedropper. */
    const lastPaintToolRef = useRef<ToolKind>("pencil");

    const refreshHasContent = useCallback(() => {
        setHasContent(countFilledCells(pixelsRef.current) > 0);
    }, []);

    const commit = useCallback(() => {
        setPixelsVersion((v) => v + 1);
        refreshHasContent();
    }, [refreshHasContent]);

    const snapshot = useCallback(() => {
        history.push(pixelsRef.current);
    }, [history]);

    const setGridSize = useCallback(
        (next: GridSize) => {
            if (next === gridSize) return;
            setGridSizeState(next);
            pixelsRef.current = new Uint32Array(next * next);
            history.clear();
            setPixelsVersion((v) => v + 1);
            setHasContent(false);
        },
        [gridSize, history],
    );

    const setTool = useCallback((next: ToolKind) => {
        setToolState((prev) => {
            if (prev !== "eyedropper") {
                lastPaintToolRef.current = prev;
            }
            return next;
        });
    }, []);

    const setBrushSize = useCallback((n: number) => {
        if (!Number.isFinite(n)) return;
        const clamped = Math.max(1, Math.min(64, Math.round(n)));
        setBrushSizeState(clamped);
    }, []);

    const setOutputMode = useCallback((m: OutputMode) => {
        setOutputModeState(m);
    }, []);

    const setOutputSize = useCallback((n: number) => {
        if (!Number.isFinite(n)) return;
        const clamped = Math.max(
            MIN_OUTPUT_SIZE,
            Math.min(MAX_OUTPUT_SIZE, Math.round(n)),
        );
        setOutputSizeState(clamped);
    }, []);

    const setShowOutputPane = useCallback((v: boolean) => {
        setShowOutputPaneState(v);
    }, []);

    const setEditorWhiteBackground = useCallback((v: boolean) => {
        setEditorWhiteBackgroundState(v);
    }, []);

    const setCellShape = useCallback((s: CellShape) => {
        setCellShapeState(s);
    }, []);

    const buildExportSvgString = useCallback((): string => {
        const w = gridSize;
        const result = renderPixels(
            pixelsRef.current,
            w,
            w,
            outputMode,
            outputSize,
            outputSize,
            cellShape,
        );
        return buildSvgDocument(result.body, outputSize, outputSize, cellShape);
    }, [gridSize, outputMode, outputSize, cellShape]);

    const downloadSvg = useCallback(() => {
        const w = gridSize;
        const doc = buildExportSvgString();
        const blob = new Blob([doc], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `pixel-svg-${w}x${w}-out-${outputSize}x${outputSize}-${Date.now()}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [buildExportSvgString, gridSize, outputSize]);

    const addRecentColor = useCallback((c: string) => {
        setRecentColors((prev) => {
            const next = [c, ...prev.filter((p) => p.toLowerCase() !== c.toLowerCase())];
            if (next.length > MAX_RECENT_COLORS) next.length = MAX_RECENT_COLORS;
            return next;
        });
    }, []);

    const setColor = useCallback(
        (c: string) => {
            setColorState(c);
            addRecentColor(c);
        },
        [addRecentColor],
    );

    const paintCell = useCallback(
        (x: number, y: number) => {
            const packed = parseHexColor(color);
            stampBrush(pixelsRef.current, gridSize, gridSize, x, y, brushSize, packed);
        },
        [color, brushSize, gridSize],
    );

    const eraseCell = useCallback(
        (x: number, y: number) => {
            stampBrush(
                pixelsRef.current,
                gridSize,
                gridSize,
                x,
                y,
                brushSize,
                EMPTY_CELL,
            );
        },
        [brushSize, gridSize],
    );

    const paintStroke = useCallback(
        (x0: number, y0: number, x1: number, y1: number) => {
            const packed = parseHexColor(color);
            bresenhamLine(x0, y0, x1, y1, (cx, cy) => {
                stampBrush(
                    pixelsRef.current,
                    gridSize,
                    gridSize,
                    cx,
                    cy,
                    brushSize,
                    packed,
                );
            });
        },
        [color, brushSize, gridSize],
    );

    const eraseStroke = useCallback(
        (x0: number, y0: number, x1: number, y1: number) => {
            bresenhamLine(x0, y0, x1, y1, (cx, cy) => {
                stampBrush(
                    pixelsRef.current,
                    gridSize,
                    gridSize,
                    cx,
                    cy,
                    brushSize,
                    EMPTY_CELL,
                );
            });
        },
        [brushSize, gridSize],
    );

    const fillAt = useCallback(
        (x: number, y: number) => {
            const packed = parseHexColor(color);
            floodFill(pixelsRef.current, gridSize, gridSize, x, y, packed);
        },
        [color, gridSize],
    );

    const commitRect = useCallback(
        (x0: number, y0: number, x1: number, y1: number) => {
            const packed = parseHexColor(color);
            fillRect(pixelsRef.current, gridSize, gridSize, x0, y0, x1, y1, packed);
        },
        [color, gridSize],
    );

    const commitEllipse = useCallback(
        (x0: number, y0: number, x1: number, y1: number) => {
            const packed = parseHexColor(color);
            fillEllipse(
                pixelsRef.current,
                gridSize,
                gridSize,
                x0,
                y0,
                x1,
                y1,
                packed,
            );
        },
        [color, gridSize],
    );

    const commitLine = useCallback(
        (x0: number, y0: number, x1: number, y1: number) => {
            const packed = parseHexColor(color);
            drawThickLine(
                pixelsRef.current,
                gridSize,
                gridSize,
                x0,
                y0,
                x1,
                y1,
                brushSize,
                packed,
            );
        },
        [color, brushSize, gridSize],
    );

    const restoreLastPaintTool = useCallback(() => {
        setToolState(lastPaintToolRef.current);
    }, []);

    const sampleColor = useCallback(
        (x: number, y: number): string | null => {
            if (x < 0 || x >= gridSize || y < 0 || y >= gridSize) return null;
            const v = pixelsRef.current[y * gridSize + x];
            if (v === EMPTY_CELL) return null;
            const r = (v >>> 16) & 0xff;
            const g = (v >>> 8) & 0xff;
            const b = v & 0xff;
            const hex = (n: number) => n.toString(16).padStart(2, "0");
            return `#${hex(r)}${hex(g)}${hex(b)}`;
        },
        [gridSize],
    );

    const applyPixelSnapshot = useCallback((snap: Uint32Array) => {
        if (snap.length === pixelsRef.current.length) {
            pixelsRef.current.set(snap);
        } else {
            pixelsRef.current = snap;
        }
    }, []);

    const undo = useCallback(() => {
        history.undo(pixelsRef.current, (snap) => {
            applyPixelSnapshot(snap);
            commit();
        });
    }, [history, applyPixelSnapshot, commit]);

    const redo = useCallback(() => {
        history.redo(pixelsRef.current, (snap) => {
            applyPixelSnapshot(snap);
            commit();
        });
    }, [history, applyPixelSnapshot, commit]);

    const clearAll = useCallback(() => {
        if (!hasContent) return;
        snapshot();
        pixelsRef.current.fill(EMPTY_CELL);
        commit();
    }, [hasContent, snapshot, commit]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            gridSize,
            color,
            brushSize,
            outputMode,
            outputSize,
            cellShape,
            showOutputPane,
            editorWhiteBackground,
            tool,
            recentColors,
        }),
        [
            gridSize,
            color,
            brushSize,
            outputMode,
            outputSize,
            cellShape,
            showOutputPane,
            editorWhiteBackground,
            tool,
            recentColors,
        ],
    );

    const applyPreset = useCallback(
        (values: Record<string, unknown>) => {
            if (typeof values.gridSize === "number") {
                const g = values.gridSize as number;
                const allowed = (GRID_SIZE_OPTIONS as readonly number[]).includes(g)
                    ? (g as GridSize)
                    : DEFAULT_GRID_SIZE;
                if (allowed !== gridSize) {
                    setGridSizeState(allowed);
                    pixelsRef.current = new Uint32Array(allowed * allowed);
                    history.clear();
                    setPixelsVersion((v) => v + 1);
                    setHasContent(false);
                }
            }
            if (typeof values.color === "string") setColorState(values.color);
            if (typeof values.brushSize === "number") {
                setBrushSizeState(
                    Math.max(1, Math.min(64, Math.round(values.brushSize as number))),
                );
            }
            if (typeof values.outputMode === "string") {
                const m = values.outputMode as OutputMode;
                if (m === "raw" || m === "greedy-rects" || m === "paths") {
                    setOutputModeState(m);
                }
            }
            if (typeof values.outputSize === "number") {
                setOutputSizeState(
                    Math.max(
                        MIN_OUTPUT_SIZE,
                        Math.min(MAX_OUTPUT_SIZE, Math.round(values.outputSize)),
                    ),
                );
            }
            if (typeof values.cellShape === "string") {
                const s = values.cellShape as CellShape;
                if ((CELL_SHAPE_OPTIONS as readonly string[]).includes(s)) {
                    setCellShapeState(s);
                }
            }
            if (typeof values.showOutputPane === "boolean") {
                setShowOutputPaneState(values.showOutputPane);
            }
            if (typeof values.editorWhiteBackground === "boolean") {
                setEditorWhiteBackgroundState(values.editorWhiteBackground);
            }
            if (typeof values.tool === "string") {
                const t = values.tool as ToolKind;
                const valid: ToolKind[] = [
                    "pencil",
                    "eraser",
                    "bucket",
                    "rect",
                    "ellipse",
                    "line",
                    "eyedropper",
                ];
                if (valid.includes(t)) setToolState(t);
            }
            if (Array.isArray(values.recentColors)) {
                const arr = (values.recentColors as unknown[]).filter(
                    (v): v is string => typeof v === "string",
                );
                setRecentColors(arr.slice(0, MAX_RECENT_COLORS));
            }
        },
        [gridSize, history],
    );

    const { resetParameters: resetParamsRaw } = useToolParamPersistence({
        toolId: "pixel-svg-canvas",
        defaultPreset: DEFAULT_PRESET,
        getPresetValues,
        applyPreset,
    });

    const resetParameters = useCallback(() => {
        history.clear();
        resetParamsRaw();
    }, [resetParamsRaw, history]);

    const value: PixelSvgCanvasState = useMemo(
        () => ({
            width: gridSize,
            height: gridSize,
            pixelsRef,
            pixelsVersion,
            tool,
            setTool,
            brushSize,
            setBrushSize,
            color,
            setColor,
            recentColors,
            outputMode,
            setOutputMode,
            outputSize,
            setOutputSize,
            cellShape,
            setCellShape,
            showOutputPane,
            setShowOutputPane,
            downloadSvg,
            buildExportSvgString,
            editorWhiteBackground,
            setEditorWhiteBackground,
            gridSize,
            setGridSize,
            hasContent,
            paintCell,
            paintStroke,
            eraseStroke,
            eraseCell,
            fillAt,
            commitRect,
            commitEllipse,
            commitLine,
            sampleColor,
            restoreLastPaintTool,
            snapshot,
            commit,
            undo,
            redo,
            canUndo: history.canUndo,
            canRedo: history.canRedo,
            clearAll,
            addRecentColor,
            applyPreset,
            getPresetValues,
            resetParameters,
        }),
        [
            gridSize,
            pixelsVersion,
            tool,
            setTool,
            brushSize,
            setBrushSize,
            color,
            setColor,
            recentColors,
            outputMode,
            setOutputMode,
            outputSize,
            setOutputSize,
            cellShape,
            setCellShape,
            showOutputPane,
            setShowOutputPane,
            downloadSvg,
            buildExportSvgString,
            editorWhiteBackground,
            setEditorWhiteBackground,
            setGridSize,
            hasContent,
            paintCell,
            paintStroke,
            eraseStroke,
            eraseCell,
            fillAt,
            commitRect,
            commitEllipse,
            commitLine,
            sampleColor,
            restoreLastPaintTool,
            snapshot,
            commit,
            undo,
            redo,
            history.canUndo,
            history.canRedo,
            clearAll,
            addRecentColor,
            applyPreset,
            getPresetValues,
            resetParameters,
        ],
    );

    return (
        <PixelSvgCanvasContext.Provider value={value}>
            {children}
        </PixelSvgCanvasContext.Provider>
    );
}
